import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { FirebaseAuthService, UserProfile } from '../services/firebaseAuthService';
import { User } from 'firebase/auth';

interface AuthContextType {
  user: UserProfile | null;
  firebaseUser: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAnonymous: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  loginAnonymously: (patientName?: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: UserProfile) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state listener
  useEffect(() => {
    const unsubscribe = FirebaseAuthService.onAuthStateChange(async (firebaseUser) => {
      setIsLoading(true);
      setFirebaseUser(firebaseUser);
      
      if (firebaseUser) {
        // User is signed in, get their profile
        try {
          const userProfile = await FirebaseAuthService.getCurrentUserProfile();
          setUser(userProfile);
          console.log('User authenticated:', userProfile);
        } catch (error) {
          console.error('Error getting user profile:', error);
          setUser(null);
        }
      } else {
        // User is signed out
        setUser(null);
        console.log('User signed out');
      }
      
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Login with email/password
  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      const userProfile = await FirebaseAuthService.loginUser(email, password);
      setUser(userProfile);
      console.log('Login successful:', userProfile);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Register new user
  const register = async (email: string, password: string, displayName: string): Promise<void> => {
    try {
      setIsLoading(true);
      const userProfile = await FirebaseAuthService.registerUser(email, password, displayName);
      setUser(userProfile);
      console.log('Registration successful:', userProfile);
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Anonymous login for patients
  const loginAnonymously = async (patientName?: string): Promise<void> => {
    try {
      setIsLoading(true);
      const userProfile = await FirebaseAuthService.loginAnonymously(patientName);
      setUser(userProfile);
      console.log('Anonymous login successful:', userProfile);
    } catch (error) {
      console.error('Anonymous login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout
  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await FirebaseAuthService.logout();
      setUser(null);
      setFirebaseUser(null);
      console.log('Logout successful');
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Update user profile
  const updateUser = (updatedUser: UserProfile): void => {
    setUser(updatedUser);
  };

  const value: AuthContextType = {
    user,
    firebaseUser,
    isAuthenticated: !!user,
    isLoading,
    isAnonymous: user?.isAnonymous || false,
    login,
    register,
    loginAnonymously,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};